<script setup>
import { ref, onMounted, computed } from 'vue';

// API-Basis-URL
const API_URL = '/api';

// Shows-Daten
const shows = ref([]);
const isLoading = ref(false);
const error = ref(null);
const successMessage = ref('');

// Ausgewählte Shows für Massenoperationen
const selectedShows = ref([]);

// Alle zukünftigen Shows ausgewählt?
const allUpcomingSelected = computed(() => {
  return upcomingShows.value.length > 0 && selectedShows.value.length === upcomingShows.value.length;
});

// Alle vergangenen Shows ausgewählt?
const allPastSelected = computed(() => {
  return pastShows.value.length > 0 && pastShows.value.every(show => selectedShows.value.includes(show.id));
});

// Prüfen, ob mindestens eine Show ausgewählt ist
const hasSelectedShows = computed(() => {
  return selectedShows.value.length > 0;
});

// Alle zukünftigen Shows auswählen/abwählen
const toggleAllUpcoming = () => {
  if (allUpcomingSelected.value) {
    // Alle abwählen
    selectedShows.value = selectedShows.value.filter(id => 
      !upcomingShows.value.some(show => show.id === id)
    );
  } else {
    // Alle auswählen
    const upcomingIds = upcomingShows.value.map(show => show.id);
    selectedShows.value = [...new Set([...selectedShows.value, ...upcomingIds])];
  }
};

// Alle vergangenen Shows auswählen/abwählen
const toggleAllPast = () => {
  if (allPastSelected.value) {
    // Alle abwählen
    selectedShows.value = selectedShows.value.filter(id => 
      !pastShows.value.some(show => show.id === id)
    );
  } else {
    // Alle auswählen
    const pastIds = pastShows.value.map(show => show.id);
    selectedShows.value = [...new Set([...selectedShows.value, ...pastIds])];
  }
};

// Einzelne Show auswählen/abwählen
const toggleShowSelection = (showId) => {
  if (selectedShows.value.includes(showId)) {
    selectedShows.value = selectedShows.value.filter(id => id !== showId);
  } else {
    selectedShows.value.push(showId);
  }
};

// Ausgewählte Shows löschen
const deleteSelectedShows = async () => {
  if (selectedShows.value.length === 0) return;
  
  if (!confirm(`Möchten Sie wirklich ${selectedShows.value.length} ausgewählte Termine löschen?`)) {
    return;
  }
  
  isLoading.value = true;
  error.value = null;
  
  try {
    const failedDeletions = [];
    
    // Sequentielles Löschen der ausgewählten Shows
    for (const id of selectedShows.value) {
      try {
        const response = await fetch(`${API_URL}/shows.php?id=${id}`, {
          method: 'DELETE'
        });
        
        const data = await response.json();
        
        if (!data.id) {
          failedDeletions.push(id);
        }
      } catch (error) {
        console.error(`Fehler beim Löschen der Show mit ID ${id}:`, error);
        failedDeletions.push(id);
      }
    }
    
    // Aktualisiere die Shows-Liste
    if (failedDeletions.length === 0) {
      shows.value = shows.value.filter(show => !selectedShows.value.includes(show.id));
      selectedShows.value = [];
      successMessage.value = `${selectedShows.value.length} Termine wurden erfolgreich gelöscht.`;
    } else {
      // Entferne erfolgreich gelöschte Shows aus der Liste
      shows.value = shows.value.filter(show => !selectedShows.value.includes(show.id) || failedDeletions.includes(show.id));
      // Behalte fehlgeschlagene Shows in der Auswahlliste
      selectedShows.value = failedDeletions;
      error.value = `${failedDeletions.length} Termine konnten nicht gelöscht werden.`;
    }
  } catch (error) {
    console.error('Fehler beim Löschen der ausgewählten Shows:', error);
    error.value = 'Es ist ein Fehler beim Löschen der Termine aufgetreten.';
  } finally {
    isLoading.value = false;
  }
};

// Vorschläge für häufig genutzte Werte
const uniqueVenues = computed(() => {
  // Extrahiere eindeutige Veranstaltungsorte aus existierenden Shows
  const venues = new Set(shows.value.map(show => show.venue));
  return Array.from(venues);
});

const uniqueLocations = computed(() => {
  // Extrahiere eindeutige Adressen/Ortsteile aus existierenden Shows
  const locations = new Set(shows.value.map(show => show.location));
  return Array.from(locations);
});

// Filtere zukünftige Termine
const upcomingShows = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Setze Zeit auf Mitternacht für genauen Tagesvergleich
  
  return shows.value.filter(show => {
    const showDate = new Date(show.date);
    return showDate >= today;
  }).sort((a, b) => new Date(a.date) - new Date(b.date)); // Sortiere nach Datum aufsteigend
});

// Vergangene Termine (für Admin-Zwecke)
const pastShows = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return shows.value.filter(show => {
    const showDate = new Date(show.date);
    return showDate < today;
  }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Sortiere nach Datum absteigend (neueste zuerst)
});

// Zeige vergangene Termine
const showPastShows = ref(false);

// Kalenderbild
const calendarImage = ref('');
const imagePreview = ref('');
const selectedFile = ref(null);

// Tab-Verwaltung
const activeTab = ref('shows'); // 'shows' oder 'calendar'

const newShow = ref({
  date: '',
  venue: '',
  location: '',
  ticketLink: '',
  show_time: ''
});

const editingShow = ref(null);
const showForm = ref(false);

// Shows laden
const loadShows = async () => {
  try {
    console.log('Starte Laden der Shows...');
    const response = await fetch(`${API_URL}/shows.php`);
    const data = await response.json();
    console.log('API Antwort:', data);
    
    if (data.success) {
      console.log('Rohdaten der Shows:', data.shows);
      shows.value = data.shows.map(show => {
        console.log('Verarbeite Show:', show);
        console.log('Datum vor Verarbeitung:', show.date);
        
        // Stelle sicher, dass das Datum im korrekten Format ist
        const dateStr = show.date.split('T')[0]; // Entferne Zeitanteil falls vorhanden
        console.log('Datum nach T-Entfernung:', dateStr);
        
        const [year, month, day] = dateStr.split('-').map(Number);
        console.log('Zerlegtes Datum:', { year, month, day });
        
        const date = new Date(year, month - 1, day);
        console.log('Erstelltes Date-Objekt:', date);
        
        const processedShow = {
          ...show,
          date: dateStr,
          day: day,
          month: month,
          monthName: date.toLocaleString('de-DE', { month: 'long' })
        };
        console.log('Verarbeitete Show:', processedShow);
        return processedShow;
      });
      console.log('Finale Shows-Liste:', shows.value);
    } else {
      console.error('Fehler beim Laden der Shows:', data.error);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Shows:', error);
  }
};

// Show hinzufügen
const addShow = async () => {
  console.log('Starte Hinzufügen einer Show...');
  console.log('Neue Show Daten:', newShow.value);
  
  if (!newShow.value.date || !newShow.value.venue || !newShow.value.location) {
    console.warn('Fehlende Pflichtfelder:', {
      date: newShow.value.date,
      venue: newShow.value.venue,
      location: newShow.value.location
    });
    alert('Bitte füllen Sie alle erforderlichen Felder aus.');
    return;
  }

  try {
    // Stelle sicher, dass das Datum im korrekten Format ist
    console.log('Datum vor Verarbeitung:', newShow.value.date);
    const dateStr = newShow.value.date.split('T')[0];
    console.log('Datum nach T-Entfernung:', dateStr);
    
    const [year, month, day] = dateStr.split('-').map(Number);
    console.log('Zerlegtes Datum:', { year, month, day });
    
    const date = new Date(year, month - 1, day);
    console.log('Erstelltes Date-Objekt:', date);

    const formattedShow = {
      ...newShow.value,
      date: dateStr,
      show_time: newShow.value.show_time || null
    };
    console.log('Formatierte Show für API:', formattedShow);

    const response = await fetch(`${API_URL}/shows.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formattedShow)
    });

    const data = await response.json();
    console.log('API Antwort:', data);
    
    if (data.id) {
      const newShowWithId = {
        ...formattedShow,
        id: data.id,
        day: day,
        month: month,
        monthName: date.toLocaleString('de-DE', { month: 'long' })
      };
      console.log('Neue Show mit ID:', newShowWithId);
      
      shows.value.push(newShowWithId);
      console.log('Aktualisierte Shows-Liste:', shows.value);
      
      newShow.value = { date: '', venue: '', location: '', ticketLink: '', show_time: '' };
    } else {
      console.error('Fehler beim Hinzufügen:', data.error);
      alert('Fehler beim Hinzufügen des Auftritts: ' + (data.error || 'Unbekannter Fehler'));
    }
  } catch (error) {
    console.error('Fehler beim Hinzufügen des Auftritts:', error);
    alert('Fehler beim Hinzufügen des Auftritts');
  }
};

// Show aktualisieren
const updateShow = async (show) => {
  console.log('Starte Aktualisierung einer Show...');
  console.log('Zu aktualisierende Show:', show);
  
  try {
    // Stelle sicher, dass das Datum im korrekten Format ist
    console.log('Datum vor Verarbeitung:', show.date);
    const dateStr = show.date.split('T')[0];
    console.log('Datum nach T-Entfernung:', dateStr);
    
    const [year, month, day] = dateStr.split('-').map(Number);
    console.log('Zerlegtes Datum:', { year, month, day });
    
    const date = new Date(year, month - 1, day);
    console.log('Erstelltes Date-Objekt:', date);

    const formattedShow = {
      date: dateStr,
      venue: show.venue,
      location: show.location,
      ticketLink: show.ticketLink,
      show_time: show.show_time || null
    };
    console.log('Formatierte Show für API:', formattedShow);

    const response = await fetch(`${API_URL}/shows.php?id=${show.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formattedShow)
    });

    const data = await response.json();
    console.log('API Antwort:', data);
    
    if (data.id) {
      const updatedShow = {
        ...show,
        ...formattedShow,
        day: day,
        month: month,
        monthName: date.toLocaleString('de-DE', { month: 'long' })
      };
      console.log('Aktualisierte Show:', updatedShow);
      
      // Aktualisiere den Show in der Liste
      const index = shows.value.findIndex(s => s.id === show.id);
      if (index !== -1) {
        shows.value[index] = updatedShow;
        console.log('Aktualisierte Shows-Liste:', shows.value);
      }
      
      // Formular zurücksetzen
      showForm.value = false;
      editingShow.value = null;
      successMessage.value = 'Termin wurde erfolgreich aktualisiert.';
      
      // Verzögertes Ausblenden der Erfolgsmeldung
      setTimeout(() => {
        successMessage.value = '';
      }, 3000);
    } else {
      console.error('Fehler beim Aktualisieren:', data.error);
      error.value = 'Fehler beim Aktualisieren des Auftritts: ' + (data.error || 'Unbekannter Fehler');
    }
  } catch (error) {
    console.error('Fehler beim Aktualisieren des Auftritts:', error);
    error.value = 'Fehler beim Aktualisieren des Auftritts';
  }
};

// Termin zum Bearbeiten vorbereiten
const editShow = (show) => {
  console.log('Öffne Bearbeitungsformular für Show:', show);
  
  // Kopie des ausgewählten Termins erstellen, um Original nicht zu verändern
  editingShow.value = { ...show };
  
  // Formularwerte mit den Werten des Termins füllen
  newShow.value = {
    date: show.date,
    venue: show.venue,
    location: show.location,
    ticketLink: show.ticketLink || '',
    show_time: show.show_time || ''
  };
  
  // Formular anzeigen
  showForm.value = true;
};

// Show löschen
const deleteShow = async (id) => {
  if (!confirm('Möchten Sie diesen Auftritt wirklich löschen?')) {
    return;
  }

  try {
    const response = await fetch(`${API_URL}/shows.php?id=${id}`, {
      method: 'DELETE'
    });

    const data = await response.json();
    
    if (data.id) {
      shows.value = shows.value.filter(show => show.id !== id);
    } else {
      alert('Fehler beim Löschen des Auftritts: ' + (data.error || 'Unbekannter Fehler'));
    }
  } catch (error) {
    console.error('Fehler beim Löschen des Auftritts:', error);
    alert('Fehler beim Löschen des Auftritts');
  }
};

// Abbrechen des Formulars
const cancelForm = () => {
  showForm.value = false;
  editingShow.value = null;
  error.value = null;
};

// Formatiere Datum für die Anzeige
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Extrahiere Tag aus dem Datum
const getDay = (dateString) => {
  const date = new Date(dateString);
  return date.getDate();
};

// Extrahiere Monat aus dem Datum
const getMonth = (dateString) => {
  const date = new Date(dateString);
  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return monthNames[date.getMonth()];
};

// Bildvorschau aktualisieren
const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  // Nur Bilder akzeptieren
  if (!file.type.match('image.*')) {
    error.value = 'Bitte wählen Sie ein Bild aus (JPEG, PNG, GIF).';
    return;
  }
  
  selectedFile.value = file;
  
  // Vorschau erstellen
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

// Kalenderbild hochladen
const saveCalendarImage = async () => {
  if (!imagePreview.value) {
    error.value = 'Bitte wählen Sie zuerst ein Bild aus.';
    return;
  }
  
  try {
    isLoading.value = true;
    error.value = null;
    successMessage.value = '';
    
    const response = await fetch(`${API_URL}/settings.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        calendar_image_upload: imagePreview.value
      })
    });
    
    const data = await response.json();
    
    if (data.success) {
      successMessage.value = 'Kalenderbild wurde erfolgreich aktualisiert.';
      calendarImage.value = data.calendar_image || '';
      selectedFile.value = null;
      
      // Verzögertes Ausblenden der Erfolgsmeldung
      setTimeout(() => {
        successMessage.value = '';
      }, 3000);
    } else {
      throw new Error(data.error || 'Fehler beim Speichern des Kalenderbildes.');
    }
  } catch (err) {
    console.error('Fehler beim Speichern des Kalenderbildes:', err);
    error.value = err.message || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
  } finally {
    isLoading.value = false;
  }
};

// Daten laden
const loadData = async () => {
  isLoading.value = true;
  try {
    // Shows laden
    await loadShows();
    
    // Kalenderbild laden
    const response = await fetch(`${API_URL}/settings.php`);
    const data = await response.json();
    
    if (data.success && data.settings && data.settings.calendar_image) {
      calendarImage.value = data.settings.calendar_image;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Daten:', error);
  } finally {
    isLoading.value = false;
  }
};

// Laden der Daten beim Initialisieren
onMounted(() => {
  loadData();
});
</script>

<template>
  <div class="shows-manager">
    <div class="section-header">
      <h2>Auftritte verwalten</h2>
      <p class="section-description">
        Hier können Sie Konzerttermine hinzufügen, bearbeiten oder löschen.
      </p>
    </div>
    
    <!-- Erfolgsmeldung -->
    <div v-if="successMessage" class="success-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <span>{{ successMessage }}</span>
    </div>
    
    <!-- Fehlermeldung -->
    <div v-if="error" class="error-message">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <span>{{ error }}</span>
    </div>
    
    <!-- Tab Navigation -->
    <div class="tabs-navigation">
      <button 
        @click="activeTab = 'shows'" 
        :class="['tab-button', { active: activeTab === 'shows' }]"
      >
        Termine
      </button>
      <button 
        @click="activeTab = 'calendar'" 
        :class="['tab-button', { active: activeTab === 'calendar' }]"
      >
        Kalenderbild
      </button>
    </div>
    
    <!-- Tab: Termine verwalten -->
    <div v-if="activeTab === 'shows'" class="admin-actions">
      <div class="admin-header">
        <div class="admin-header-actions">
          <button @click="showForm = true" class="btn btn-primary" :disabled="isLoading">
            <span v-if="!isLoading">Neuen Termin hinzufügen</span>
            <span v-else>Laden...</span>
          </button>
          
          <!-- Massenaktion-Leiste -->
          <div class="bulk-actions" v-if="hasSelectedShows">
            <span class="selected-count">{{ selectedShows.length }} ausgewählt</span>
            <button @click="deleteSelectedShows" class="btn btn-danger" :disabled="isLoading">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
              Auswahl löschen
            </button>
          </div>
        </div>
      </div>
      
      <div v-if="showForm" class="show-form-container">
        <div class="form-card">
          <h3>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {{ editingShow ? 'Termin bearbeiten' : 'Neuen Termin hinzufügen' }}
          </h3>
          
          <form @submit.prevent="editingShow ? updateShow({...editingShow, ...newShow}) : addShow()">
            <div class="form-row">
              <div class="form-group">
                <label for="date">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Datum
                </label>
                <input 
                  type="date" 
                  id="date" 
                  v-model="newShow.date" 
                  required
                  class="form-input"
                />
              </div>
              
              <div class="form-group">
                <label for="time">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  Uhrzeit
                </label>
                <input 
                  type="time" 
                  id="time" 
                  v-model="newShow.show_time" 
                  placeholder="20:00"
                  class="form-input"
                />
              </div>
            </div>
            
            <div class="form-group">
              <label for="venue">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Veranstaltungsort
              </label>
              <input 
                type="text" 
                id="venue" 
                v-model="newShow.venue" 
                list="venue-suggestions"
                required
                class="form-input"
                placeholder="z.B. Kulturhaus"
              />
              <datalist id="venue-suggestions">
                <option v-for="venue in uniqueVenues" :key="venue" :value="venue"></option>
              </datalist>
            </div>
            
            <div class="form-group">
              <label for="location">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Adresse/Ortsteil
              </label>
              <input 
                type="text" 
                id="location" 
                v-model="newShow.location" 
                list="location-suggestions"
                required
                class="form-input"
                placeholder="z.B. Hauptstraße 1, 12345 Stadt"
              />
              <datalist id="location-suggestions">
                <option v-for="location in uniqueLocations" :key="location" :value="location"></option>
              </datalist>
            </div>
            
            <div class="form-group">
              <label for="ticketLink">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
                Ticket-Link (optional)
              </label>
              <input 
                type="text" 
                id="ticketLink" 
                v-model="newShow.ticketLink" 
                placeholder="https://"
                class="form-input"
              />
            </div>
            
            <div class="form-actions">
              <button type="button" @click="cancelForm" class="btn btn-outline" :disabled="isLoading">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Abbrechen
              </button>
              <button type="submit" class="btn btn-primary" :disabled="isLoading">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                {{ isLoading ? 'Speichere...' : (editingShow ? 'Aktualisieren' : 'Speichern') }}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <div v-if="!showForm">
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <p>Daten werden geladen...</p>
        </div>
        
        <div v-else-if="upcomingShows.length === 0 && pastShows.length === 0" class="empty-state">
          <p>Es sind noch keine Termine vorhanden.</p>
          <button @click="showForm = true" class="btn btn-primary">
            Ersten Termin hinzufügen
          </button>
        </div>
        
        <div v-else>
          <!-- Zukünftige Termine -->
          <div class="section-header-with-actions">
            <h3 class="section-title">Zukünftige Termine</h3>
            <div class="select-all-action" v-if="upcomingShows.length > 0">
              <label class="checkbox-container">
                <input type="checkbox" :checked="allUpcomingSelected" @change="toggleAllUpcoming">
                <span class="checkmark"></span>
                <span class="select-all-label">Alle auswählen</span>
              </label>
            </div>
          </div>
          
          <div v-if="upcomingShows.length === 0" class="empty-notice">
            Keine zukünftigen Termine vorhanden.
          </div>
          <div v-else class="shows-list">
            <div v-for="show in upcomingShows" :key="show.id" class="show-item" :class="{ 'selected': selectedShows.includes(show.id) }">
              <div class="show-checkbox">
                <label class="checkbox-container">
                  <input type="checkbox" 
                    :checked="selectedShows.includes(show.id)" 
                    @change="toggleShowSelection(show.id)"
                  >
                  <span class="checkmark"></span>
                </label>
              </div>
              
              <div class="show-date">
                <span class="day">{{ getDay(show.date) }}</span>
                <span class="month">{{ getMonth(show.date) }}</span>
              </div>
              
              <div class="show-info">
                <h4>{{ show.venue }}</h4>
                <p>{{ show.location }}</p>
                <p v-if="show.show_time" class="show-time">
                  <i class="far fa-clock"></i> {{ show.show_time }} Uhr
                </p>
                <p class="show-ticket" v-if="show.ticketLink">
                  Ticket-Link: <a :href="show.ticketLink" target="_blank">{{ show.ticketLink }}</a>
                </p>
              </div>
              
              <div class="show-actions">
                <button @click="editShow(show)" class="btn-action edit" :disabled="isLoading">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
                <button @click="deleteShow(show.id)" class="btn-action delete" :disabled="isLoading">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Vergangene Termine (klappbar) -->
          <div class="past-shows-section">
            <div class="past-shows-header" @click="showPastShows = !showPastShows">
              <h3 class="section-title">Vergangene Termine</h3>
              <div class="past-header-actions">
                <div class="select-all-action" v-if="showPastShows && pastShows.length > 0">
                  <label class="checkbox-container">
                    <input type="checkbox" :checked="allPastSelected" @change="toggleAllPast">
                    <span class="checkmark"></span>
                    <span class="select-all-label">Alle auswählen</span>
                  </label>
                </div>
                
                <button class="toggle-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ rotated: showPastShows }">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
            </div>
            
            <div v-if="showPastShows" class="shows-list past-shows">
              <div v-if="pastShows.length === 0" class="empty-notice">
                Keine vergangenen Termine vorhanden.
              </div>
              <div v-for="show in pastShows" :key="show.id" class="show-item past" :class="{ 'selected': selectedShows.includes(show.id) }">
                <div class="show-checkbox">
                  <label class="checkbox-container">
                    <input type="checkbox" 
                      :checked="selectedShows.includes(show.id)" 
                      @change="toggleShowSelection(show.id)"
                    >
                    <span class="checkmark"></span>
                  </label>
                </div>
                
                <div class="show-date">
                  <span class="day">{{ getDay(show.date) }}</span>
                  <span class="month">{{ getMonth(show.date) }}</span>
                </div>
                
                <div class="show-info">
                  <h4>{{ show.venue }}</h4>
                  <p>{{ show.location }}</p>
                  <p v-if="show.show_time" class="show-time">
                    <i class="far fa-clock"></i> {{ show.show_time }} Uhr
                  </p>
                  <p class="show-ticket" v-if="show.ticketLink">
                    Ticket-Link: <a :href="show.ticketLink" target="_blank">{{ show.ticketLink }}</a>
                  </p>
                </div>
                
                <div class="show-actions">
                  <button @click="editShow(show)" class="btn-action edit" :disabled="isLoading">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button @click="deleteShow(show.id)" class="btn-action delete" :disabled="isLoading">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Tab: Kalenderbild -->
    <div v-if="activeTab === 'calendar'" class="admin-actions">
      <div class="form-card calendar-image-section">
        <h3>Kalenderbild</h3>
        <div class="form-description">
          <p>Dieses Bild wird neben der Terminliste auf der Frontseite angezeigt.</p>
        </div>
        
        <div class="form-group">
          <div class="image-preview-container">
            <img 
              :src="imagePreview || calendarImage || '/placeholder.jpg'" 
              alt="Kalenderbild-Vorschau" 
              class="image-preview"
            >
          </div>
          
          <div class="file-input-wrapper">
            <label for="calendar-image" class="file-label">Bild auswählen</label>
            <input 
              type="file" 
              id="calendar-image" 
              @change="handleFileChange" 
              accept="image/*" 
              class="file-input"
            >
            <span class="selected-file">{{ selectedFile ? selectedFile.name : 'Keine Datei ausgewählt' }}</span>
          </div>
          
          <button @click.prevent="saveCalendarImage" class="btn-save" :disabled="isLoading || !imagePreview">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            Bild speichern
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shows-manager {
  padding: 1.5rem;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eaeaea;
}

.admin-header h2 {
  margin: 0;
  color: var(--primary-color);
}

.error-banner {
  background-color: #ffebee;
  color: #e53935;
  padding: 1rem;
  border-radius: var(--radius-sm);
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-close {
  background: none;
  border: none;
  color: #e53935;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 132, 61, 0.1);
  border-left-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.show-form-container {
  margin-bottom: 2rem;
}

.form-card {
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  padding: 2rem;
  margin-bottom: 2rem;
}

.form-card h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
}

.form-group label svg {
  color: var(--primary-color);
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  transition: all 0.3s ease;
  background-color: var(--white);
  color: var(--text-color);
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(0, 132, 61, 0.1);
}

.form-input::placeholder {
  color: var(--text-muted);
  opacity: 0.7;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-sm);
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn svg {
  width: 16px;
  height: 16px;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-dark);
}

.btn-outline {
  background-color: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-color);
}

.btn-outline:hover:not(:disabled) {
  background-color: var(--bg-alt);
  border-color: var(--text-muted);
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}

.shows-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.show-item {
  display: flex;
  align-items: center;
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
  transition: var(--transition-normal);
}

.show-item:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.show-date {
  min-width: 70px;
  text-align: center;
  margin-right: 1.5rem;
}

.show-date .day {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1.2;
}

.show-date .month {
  display: block;
  font-size: 0.9rem;
  color: var(--text-muted);
}

.show-info {
  flex: 1;
}

.show-info h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.show-info p {
  margin: 0;
  color: var(--text-muted);
  font-size: 0.9rem;
}

.show-ticket {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.show-time {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
}

.show-time i {
  margin-right: 0.5rem;
  color: var(--primary-color);
}

.show-actions {
  display: flex;
  gap: 0.75rem;
}

.btn-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--text-muted);
  transition: var(--transition-normal);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-action.edit:hover:not(:disabled) {
  color: var(--primary-color);
  background-color: rgba(0, 132, 61, 0.1);
}

.btn-action.delete:hover:not(:disabled) {
  color: #e53935;
  background-color: rgba(229, 57, 53, 0.1);
}

.empty-state {
  text-align: center;
  padding: 3rem;
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.empty-state p {
  margin-bottom: 1.5rem;
  color: var(--text-muted);
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.calendar-image-section {
  margin-bottom: 2rem;
}

.image-preview-container {
  width: 100%;
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.image-preview {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  display: block;
}

.file-input-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.file-label {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: var(--primary-color);
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.file-label:hover {
  background-color: var(--primary-dark);
}

.file-input {
  position: absolute;
  left: -9999px;
}

.selected-file {
  font-size: 0.9rem;
  color: var(--text-muted);
}

.success-message,
.error-message {
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.success-message {
  background-color: rgba(0, 128, 0, 0.1);
  color: green;
  border: 1px solid rgba(0, 128, 0, 0.2);
}

.error-message {
  background-color: rgba(255, 0, 0, 0.1);
  color: red;
  border: 1px solid rgba(255, 0, 0, 0.2);
}

.form-description {
  margin-bottom: 1rem;
  color: var(--text-muted);
}

/* Tab Styles */
.tabs-navigation {
  display: flex;
  margin-bottom: 2rem;
  border-bottom: 1px solid #eaeaea;
}

.tab-button {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.tab-button:hover {
  color: var(--primary-color);
}

.tab-button.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

/* Datalist Styling */
input::-webkit-calendar-picker-indicator {
  opacity: 0.4;
  cursor: pointer;
}

input:focus::-webkit-calendar-picker-indicator {
  opacity: 0.7;
}

.section-title {
  margin: 1.5rem 0 1rem;
  color: var(--primary-color);
  font-size: 1.2rem;
}

.empty-notice {
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: var(--radius-md);
  text-align: center;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.past-shows-section {
  margin-top: 2rem;
  border-top: 1px solid #eaeaea;
  padding-top: 1rem;
}

.past-shows-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
}

.toggle-btn svg {
  transition: transform 0.3s ease;
}

.toggle-btn svg.rotated {
  transform: rotate(180deg);
}

.show-item.past {
  opacity: 0.7;
}

.show-item.past:hover {
  opacity: 0.9;
}

.admin-header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.selected-count {
  font-size: 0.9rem;
  color: var(--text-muted);
  font-weight: 500;
}

.btn-danger {
  background-color: #e53935;
  border-color: #e53935;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-danger:hover {
  background-color: #c62828;
  border-color: #c62828;
}

.section-header-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.select-all-action {
  font-size: 0.9rem;
}

.past-header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.show-item {
  position: relative;
  padding-left: 3rem;
}

.show-item.selected {
  background-color: rgba(0, 132, 61, 0.05);
  border-left: 3px solid var(--primary-color);
}

.show-checkbox {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
}

/* Checkbox styling */
.checkbox-container {
  display: inline-block;
  position: relative;
  padding-left: 25px;
  margin-right: 12px;
  cursor: pointer;
  user-select: none;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  width: 18px;
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 3px;
}

.checkbox-container:hover input ~ .checkmark {
  background-color: #e0e0e0;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 6px;
  top: 2px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.select-all-label {
  margin-left: 5px;
  font-size: 0.9rem;
  color: var(--text-muted);
}
</style> 